import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { dateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { W9Form } from "@/components/W9Form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube, FileText, Key, Check, CreditCard, Bank, PencilSimple, ShieldCheck, Database } from "@phosphor-icons/react";

function Card({ icon: Icon, title, desc, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-border bg-card p-5">
      <div className="mb-4 flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-primary/15 text-primary"><Icon size={18} weight="fill" /></div>
        <div>
          <h4 className="font-heading text-base font-semibold">{title}</h4>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = ref.current.getContext("2d");
    const p = pos(e); c.lineWidth = 2.2; c.lineCap = "round"; c.strokeStyle = "#e8eef5"; c.lineTo(p.x, p.y); c.stroke();
    dirty.current = true;
  };
  const end = () => { if (drawing.current) { drawing.current = false; if (dirty.current) onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); dirty.current = false; onChange(""); };

  return (
    <div className="space-y-2">
      <canvas ref={ref} width={520} height={130} data-testid="signature-canvas"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full cursor-crosshair rounded-sm border border-border bg-background" />
      <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
    </div>
  );
}

export default function OnboardingTab({ rep, onReload }) {
  const repId = rep.id;
  const [prov, setProv] = useState(undefined);
  const [contract, setContract] = useState(undefined);
  const [connect, setConnect] = useState(undefined);
  const [signer, setSigner] = useState(rep.name);
  const [sigMode, setSigMode] = useState("typed");
  const [drawn, setDrawn] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const [depositUrl, setDepositUrl] = useState(null);
  const [depositOpen, setDepositOpen] = useState(false);

  const load = () => {
    api.get(`/reps/${repId}/provision`).then((r) => setProv(r.data)).catch(() => setProv(null));
    api.get(`/reps/${repId}/contract`).then((r) => setContract(r.data)).catch(() => setContract(null));
    api.get(`/reps/${repId}/connect/status`).then((r) => setConnect(r.data)).catch(() => setConnect(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [repId]);

  const act = async (fn, key) => { setBusy(key); try { await fn(); } finally { setBusy(""); } };

  const connectStripe = () => act(async () => {
    try { const r = await api.post(`/reps/${repId}/connect/onboard`); window.open(r.data.url, "_blank"); toast.success("Opened Stripe onboarding in a new tab"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, "connect");
  const refreshConnect = () => act(async () => {
    const r = await api.get(`/reps/${repId}/connect/status`); setConnect(r.data); onReload && onReload();
  }, "refreshconnect");

  const requestProv = () => act(async () => { await api.post(`/reps/${repId}/provision`); toast.success("Provisioning requested — awaiting approval"); load(); }, "reqprov");
  const approveProv = () => act(async () => { await api.post(`/reps/${repId}/provision/approve`); toast.success("Provisioned — GHL sub-account created"); load(); onReload && onReload(); }, "approveprov");

  const genContract = () => act(async () => {
    try { const r = await api.post(`/reps/${repId}/contract/generate`); setContract(r.data); toast.success("Contract generated & auto-filled"); onReload && onReload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, "gencontract");

  const sign = () => act(async () => {
    const typed = sigMode === "typed";
    if (typed && !signer.trim()) { toast.error("Type your full name to sign"); return; }
    if (!typed && !drawn) { toast.error("Draw your signature to sign"); return; }
    try {
      const r = await api.post(`/contracts/${contract.id}/sign`, {
        signature_type: sigMode, signature_value: typed ? signer : drawn, signer_name: signer || rep.name });
      toast.success("Signed — rep is now active and earning");
      // P0: route straight into direct-deposit setup at the moment of highest engagement
      if (r.data.connect_onboarding_url) { setDepositUrl(r.data.connect_onboarding_url); setDepositOpen(true); }
      load(); onReload && onReload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, "sign");

  const createLogin = () => act(async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.post(`/reps/${repId}/create-login`, { password: pw }); toast.success(`Login created for ${rep.email}`); setPw(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, "login");

  const [ds, setDs] = useState({
    data_source: rep.data_source || "ghl", votel_sub_account_id: rep.votel_sub_account_id || "",
    votel_api_key: rep.votel_api_key || "", votel_affiliate_code: rep.votel_affiliate_code || "",
    votel_tracking_link: rep.votel_tracking_link || "", votel_login_url: rep.votel_login_url || "",
  });
  const saveDataSource = () => act(async () => {
    try { await api.put(`/reps/${repId}`, ds); toast.success(`Data source set to ${ds.data_source === "votel" ? "LocalPro OS" : "Legacy (GHL)"}`); onReload && onReload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, "datasource");

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card icon={Cube} title="GHL Provisioning" desc="Idempotent · owner-approved · clones the Kaden template" testid="provision-card">
        {prov === undefined ? <p className="text-sm text-muted-foreground">Loading…</p> : !prov ? (
          <Button data-testid="request-provision-btn" onClick={requestProv} disabled={busy === "reqprov"}>Request Provisioning</Button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2"><StatusBadge status={prov.status === "provisioned" ? "active" : "pending"} /><span className="text-xs text-muted-foreground">{prov.status}</span></div>
            {prov.status === "pending_approval" && (
              <Button data-testid="approve-provision-btn" onClick={approveProv} disabled={busy === "approveprov"}>Approve &amp; Provision</Button>
            )}
            {prov.assets && (
              <div className="space-y-1 rounded-sm border border-border bg-background p-3 font-mono text-xs">
                <div>sub-account: <span className="text-primary">{prov.assets.ghl_sub_account_id}</span></div>
                <div>setter agent: {prov.assets.setter_agent_id}</div>
                <div>calendar: {prov.assets.booking_calendar_id}</div>
                <div>tag: {prov.source_snapshot?.partner_tag}</div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card icon={Database} title="Data Source" desc="Which CRM feeds this rep's won deals — Legacy (GHL) or LocalPro OS" testid="datasource-card">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="overline">Deal data source</Label>
            <Select value={ds.data_source} onValueChange={(v) => setDs({ ...ds, data_source: v })}>
              <SelectTrigger data-testid="ds-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ghl">Legacy (GHL)</SelectItem>
                <SelectItem value="votel">LocalPro OS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {ds.data_source === "votel" && (
            <div className="space-y-2" data-testid="ds-votel-fields">
              {[["votel_sub_account_id", "LocalPro OS Sub-account ID"], ["votel_api_key", "LocalPro OS API Key"],
                ["votel_affiliate_code", "LocalPro OS Affiliate Code"], ["votel_tracking_link", "LocalPro OS Tracking Link"],
                ["votel_login_url", "LocalPro OS Login URL"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="overline">{label}</Label>
                  <Input data-testid={`ds-${k}`} value={ds[k]} onChange={(e) => setDs({ ...ds, [k]: e.target.value })} className="font-mono" />
                </div>
              ))}
            </div>
          )}
          <Button data-testid="ds-save-btn" onClick={saveDataSource} disabled={busy === "datasource"}>Save data source</Button>
        </div>
      </Card>

      <Card icon={Key} title="Rep Login" desc="Restricted read-only portal account (role: rep)" testid="login-card">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="overline">Temp Password</Label>
            <Input data-testid="rep-login-password" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min 6 chars" className="font-mono" />
          </div>
          <Button data-testid="create-login-btn" variant="outline" onClick={createLogin} disabled={busy === "login"}>Create Login ({rep.email})</Button>
        </div>
      </Card>

      <Card icon={CreditCard} title="Stripe Payouts" desc="Rep = connected account · gated on payouts_enabled" testid="stripe-card">
        <div className="space-y-3">
          <div className="flex items-center gap-2" data-testid="stripe-status">
            <StatusBadge status={connect?.payouts_enabled ? "active" : connect?.connected ? "pending" : "unsigned"} />
            <span className="font-mono text-xs text-muted-foreground">
              {connect === undefined ? "…" : connect?.payouts_enabled ? "payouts enabled" : connect?.connected ? "onboarding incomplete" : "not connected"}
            </span>
          </div>
          {connect?.error && <p className="font-mono text-[11px] text-muted-foreground">{connect.error}</p>}
          <div className="flex gap-2">
            <Button data-testid="connect-stripe-btn" variant="outline" onClick={connectStripe} disabled={busy === "connect"}>
              {connect?.connected ? "Continue Onboarding" : "Connect Stripe"}
            </Button>
            <Button variant="ghost" onClick={refreshConnect} disabled={busy === "refreshconnect"}>Refresh</Button>
          </div>
        </div>
      </Card>

      <div className="lg:col-span-2">
        <Card icon={ShieldCheck} title="W-9 / Taxpayer Info" desc="Encrypted at rest · required before first payout · feeds year-end 1099-NEC" testid="w9-card">
          <W9Form base={`/reps/${repId}`} defaultName={rep.name} onSaved={() => { load(); onReload && onReload(); }} />
        </Card>
      </div>

      <div className="lg:col-span-2">
        <Card icon={FileText} title="Contract" desc="Auto-filled from this rep's profile · signature gates activation" testid="contract-card">
          <div className="mb-3 flex items-center gap-3">
            <Button data-testid="generate-contract-btn" onClick={genContract} disabled={busy === "gencontract"}>
              {contract ? "Regenerate" : "Generate Contract"}
            </Button>
            {contract && <StatusBadge status={contract.status} />}
            {contract?.status === "signed" && (
              <span className="font-mono text-xs text-muted-foreground">
                Signed by {contract.signature?.name} · {dateTime(contract.signed_at)} · IP {contract.signed_ip} · {contract.signature?.type}
              </span>
            )}
          </div>

          {contract === undefined ? <p className="text-sm text-muted-foreground">Loading…</p> : !contract ? (
            <p className="text-sm text-muted-foreground">No contract yet. A locked template must exist (Contract Template page). Then generate to auto-fill this rep's terms.</p>
          ) : (
            <>
              <ScrollArea className="h-72 rounded-sm border border-border bg-background p-4">
                <pre data-testid="contract-body" className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{contract.rendered_body}</pre>
              </ScrollArea>
              {contract.status !== "signed" && (
                <div className="mt-4 space-y-3 rounded-sm border border-border p-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant={sigMode === "typed" ? "default" : "outline"} className="gap-1" onClick={() => setSigMode("typed")} data-testid="sig-mode-typed"><Check size={13} /> Type</Button>
                    <Button size="sm" variant={sigMode === "drawn" ? "default" : "outline"} className="gap-1" onClick={() => setSigMode("drawn")} data-testid="sig-mode-drawn"><PencilSimple size={13} /> Draw</Button>
                  </div>
                  {sigMode === "typed" ? (
                    <div className="space-y-1.5">
                      <Label className="overline">Typed Signature (full legal name)</Label>
                      <Input data-testid="signature-input" value={signer} onChange={(e) => setSigner(e.target.value)} className="font-mono" />
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="overline">Draw your signature</Label>
                      <SignaturePad onChange={setDrawn} />
                      <Input data-testid="signer-name-input" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Full legal name" className="font-mono" />
                    </div>
                  )}
                  <Button data-testid="sign-contract-btn" onClick={sign} disabled={busy === "sign"} className="gap-2">
                    <Check size={16} weight="bold" /> Sign &amp; Activate
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* P0: direct-deposit handoff right after signing */}
      <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
        <DialogContent className="max-w-md" data-testid="deposit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Bank size={20} weight="fill" /> Set up direct deposit</DialogTitle>
            <DialogDescription>
              You're signed and active. Add bank details now (via Stripe) so payouts land in your account. Bank info goes straight to Stripe — we never store it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button variant="ghost" onClick={() => setDepositOpen(false)} data-testid="deposit-skip-btn">Skip for now</Button>
            <Button data-testid="deposit-continue-btn" onClick={() => { window.open(depositUrl, "_blank"); setDepositOpen(false); load(); }}>Set up direct deposit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
